import { merge } from "lodash";
import React, { useEffect, useState } from "react";
import { BaseOptionChart } from "../../../components/chart";
import { selectConversionStatsByCompany } from "../../../core/redux/features/stats/statsSlice";
import { useAppSelector } from "../../../core/redux/hooks";
import { Card, Stack, CardHeader, Select, MenuItem, Box } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { sortFunc } from "./ClicksOnApplyNowByCountry";
import { selectCompanies } from "../../../core/redux/features/companies/companiesSlice";
import CustomAutocomplete from "../Jobs/CustomAutocomplete";

type Props = {};

const AppWebsiteConversions = (props: Props) => {
  const conversions = useAppSelector(selectConversionStatsByCompany);
  const companies = useAppSelector(selectCompanies);

  const [projection, setProjection] = useState("yearly");
  const [activeCompanies, setActiveCompanies] = useState<string[]>([
    companies[0]._id,
  ]);
  const [chartLabelsState, setChartLabelsState] = useState<any>();
  const [chartDataState, setChartDataState] = useState<any>();

  //   const sortedConversions = [...conversions.fin].sort(
  //     (a, b) => a.createdAt - b.createdAt
  //   );

  //   const allDates = sortedConversions.map((i) => i.createdAt);

  useEffect(() => {
    if (projection === "yearly") {
      const labels: any[] = [];
      const data = conversions
        .filter((item) => activeCompanies.includes(item._id))
        .map((item) => {
          labels.push(...item.stats.map((i) => i.createdAt).sort(sortFunc));

          return {
            name: item.name,
            type: "area",
            fill: "gradient",
            data: item.stats.map((i) => i.conversion_rate),
          };
        });
      setChartLabelsState(labels.map((item) => new Date(item).toDateString()));
      setChartDataState(data);
    } else if (projection === "90-days") {
      const labels: any[] = [];
      const data = conversions
        .filter((item) => activeCompanies.includes(item._id))
        .map((item) => {
          labels.push(
            ...item.stats
              .map((i) => i.createdAt)
              .sort(sortFunc)
              .slice(-90)
          );
          return {
            name: item.name,
            type: "area",
            fill: "gradient",
            data: item.stats.map((i) => i.conversion_rate).slice(-90),
          };
        });
      setChartLabelsState(labels.map((item) => new Date(item).toDateString()));
      setChartDataState(data);
    } else if (projection === "30-days") {
      const labels: any[] = [];
      const data = conversions
        .filter((item) => activeCompanies.includes(item._id))
        .map((item) => {
          labels.push(
            ...item.stats
              .map((i) => i.createdAt)
              .sort(sortFunc)
              .slice(-30)
          );
          return {
            name: item.name,
            type: "area",
            fill: "gradient",
            data: item.stats.map((i) => i.conversion_rate).slice(-30),
          };
        });
      setChartLabelsState(labels.map((item) => new Date(item).toDateString()));
      setChartDataState(data);
    } else if (projection === "14-days") {
      const labels: any[] = [];
      const data = conversions
        .filter((item) => activeCompanies.includes(item._id))
        .map((item) => {
          labels.push(
            ...item.stats
              .map((i) => i.createdAt)
              .sort(sortFunc)
              .slice(-14)
          );
          return {
            name: item.name,
            type: "area",
            fill: "gradient",
            data: item.stats.map((i) => i.conversion_rate).slice(-14),
          };
        });
      setChartLabelsState(labels.map((item) => new Date(item).toDateString()));
      setChartDataState(data);
    } else if (projection === "7-days") {
      const labels: any[] = [];
      const data = conversions
        .filter((item) => activeCompanies.includes(item._id))
        .map((item) => {
          labels.push(
            ...item.stats
              .map((i) => i.createdAt)
              .sort(sortFunc)
              .slice(-7)
          );
          return {
            name: item.name,
            type: "area",
            fill: "gradient",
            data: item.stats.map((i) => i.conversion_rate).slice(-7),
          };
        });
      setChartLabelsState(labels.map((item) => new Date(item).toDateString()));
      setChartDataState(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection, activeCompanies]);

  const chartOptions: any = merge(BaseOptionChart(), {
    plotOptions: { bar: { columnWidth: "16%" } },
    fill: { type: chartDataState?.map((i: any) => i.fill) },
    labels: chartLabelsState,
    xaxis: { type: "datetime" },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y: number) => {
          if (typeof y !== "undefined") {
            return `${y.toFixed(0)} applications`;
          }
          return y;
        },
      },
    },
  });

  const handleProjection = (e: any) => {
    setProjection(e.target.value);
  };

  return (
    <Card>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
        padding="10px"
      >
        <CardHeader title={"Conversions"} />

        <Stack direction="row" gap={2}>
          <CustomAutocomplete
            label="Companies"
            name="companies"
            placeholder="Search companies"
            values={activeCompanies}
            setValue={setActiveCompanies}
            time={480}
            data={companies}
          />

          <Select value={projection} onChange={handleProjection}>
            <MenuItem value={"yearly"}>In months</MenuItem>
            <MenuItem value={"90-days"}>last 90 days</MenuItem>
            <MenuItem value={"30-days"}>last 30 days</MenuItem>
            <MenuItem value={"14-days"}>last 14 days</MenuItem>
            <MenuItem value={"7-days"}>last 7 days</MenuItem>
          </Select>
        </Stack>
      </Stack>

      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        {chartDataState && (
          <ReactApexChart
            type="line"
            series={chartDataState}
            options={chartOptions}
            height={364}
            width={"100%"}
          />
        )}
      </Box>
    </Card>
  );
};

export default AppWebsiteConversions;
